import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Smartphone, Briefcase, Check, ArrowRight } from 'lucide-react';

const Services = () => {
  const [activeTab, setActiveTab] = useState(0);

  const services = [
    {
      id: 'web',
      icon: <Monitor className="w-10 h-10 text-svnate-lightTeal" />,
      title: 'Web Solutions',
      description: 'We engineer high-performance web applications, striking marketing sites, and complex administrative dashboards. Built on cutting-edge stacks to ensure lightning-fast load times and infinite scalability.',
      features: [
        'Responsive Web Design',
        'Complex Admin Dashboards',
        'High-Conversion E-commerce',
        'Progressive Web Apps (PWA)',
        'SEO-Optimized Architecture'
      ]
    },
    {
      id: 'app',
      icon: <Smartphone className="w-10 h-10 text-svnate-lightTeal" />,
      title: 'App Solutions',
      description: 'Native and cross-platform mobile applications for iOS and Android. We deliver native-like performance and intuitive UX that keeps users engaged and coming back.',
      features: [
        'Native iOS & Android Apps',
        'React Native & Flutter',
        'Rapid MVP Development',
        'App Store Optimization',
        'Ongoing App Maintenance'
      ]
    },
    {
      id: 'business',
      icon: <Briefcase className="w-10 h-10 text-svnate-lightTeal" />,
      title: 'Business Solutions',
      description: 'Bespoke internal tools and automation software designed to eliminate bottlenecks, streamline operations, and save your team thousands of hours.',
      features: [
        'Workflow Automation',
        'Custom Internal Tools',
        'Bespoke CRM Systems',
        'Enterprise Resource Planning',
        'Complex API Integrations'
      ]
    }
  ];

  return (
    <section className="bg-svnate-darkNavy py-24 md:py-32 px-6 md:px-12 relative" id="services">
      <div className="container mx-auto relative z-10 max-w-7xl flex flex-col items-center">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-12 md:mb-16 text-center"
        >
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-white/10 bg-[#0f1419]">
            <span className="text-svnate-lightTeal font-semibold text-sm">Our Expertise</span>
          </div>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6">
            Comprehensive Capabilities
          </h2>
          <p className="text-lg md:text-2xl text-foreground/70 max-w-3xl mx-auto font-serif">
            We deliver end-to-end digital solutions tailored to your unique business needs, ensuring high performance and exceptional user experiences.
          </p>
        </motion.div>

        {/* Sleek Pill Tabs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-2 p-2 bg-[#0f1419] border border-white/10 rounded-full mb-12 shadow-2xl"
        >
          {services.map((service, index) => (
            <button
              key={service.id}
              onClick={() => setActiveTab(index)}
              className={`relative px-6 md:px-10 py-3 md:py-4 rounded-full text-sm md:text-base font-bold transition-colors duration-300 ${
                activeTab === index ? 'text-white' : 'text-white/50 hover:text-white/80'
              }`}
            >
              {activeTab === index && (
                <motion.div
                  layoutId="activeTabPill"
                  className="absolute inset-0 bg-white/[0.08] border border-white/10 rounded-full"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span className="relative z-10">{service.title}</span>
            </button>
          ))}
        </motion.div>

        {/* Dynamic Content Canvas */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-6xl bg-[#0f1419] border border-white/10 rounded-[2rem] p-8 md:p-16 relative overflow-hidden min-h-[500px] flex items-center shadow-[0_0_40px_rgba(0,0,0,0.3)]"
        >
          {/* Subtle background glow based on active tab */}
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-svnate-teal/5 rounded-full blur-[100px] pointer-events-none" />

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 15, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -15, filter: 'blur(8px)' }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24 w-full relative z-10"
            >
              
              {/* Left Side: Copy */}
              <div className="flex-1 text-center lg:text-left">
                <div className="w-20 h-20 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-8 mx-auto lg:mx-0 shadow-lg">
                  {services[activeTab].icon}
                </div>
                
                <h3 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight">
                  {services[activeTab].title}
                </h3>
                
                <p className="text-lg md:text-xl text-foreground/70 font-serif leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0">
                  {services[activeTab].description}
                </p>
                
                <button 
                  onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center gap-3 font-semibold text-white group"
                >
                  <span className="border-b border-white/30 pb-1 group-hover:border-white transition-colors">
                    View Related Work
                  </span>
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors group-hover:translate-x-1">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              </div>

              {/* Right Side: Features List */}
              <div className="flex-1 w-full max-w-md bg-black/40 rounded-3xl p-8 md:p-10 border border-white/5 shadow-inner">
                <div className="text-xs uppercase tracking-widest text-white/40 font-bold mb-8">
                  Core Deliverables
                </div>
                <ul className="space-y-6">
                  {services[activeTab].features.map((feature, i) => (
                    <motion.li 
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1, duration: 0.4 }}
                      className="flex items-center gap-5 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:bg-svnate-teal/20 group-hover:border-svnate-teal/50 transition-colors duration-300">
                        <Check className="w-5 h-5 text-white/50 group-hover:text-svnate-teal transition-colors duration-300" />
                      </div>
                      <span className="text-white/80 font-medium text-lg group-hover:text-white transition-colors duration-300">
                        {feature}
                      </span>
                    </motion.li>
                  ))}
                </ul>
              </div>

            </motion.div>
          </AnimatePresence>
        </motion.div>

      </div>
    </section>
  );
};

export default Services;
